import { useState, useEffect, useCallback } from 'react';
import { Table, Tag, Space, Select, DatePicker, Button, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { tenantService, AuditLogEntry } from '../../services/tenant.service';

const { Text } = Typography;
const { RangePicker } = DatePicker;

interface TenantActivityLogProps {
  tenantId: string;
}

const actionTagColor: Record<string, string> = {
  impersonate: 'purple',
  sync_misa: 'blue',
  login: 'green',
  create: 'cyan',
  update: 'orange',
  delete: 'red',
};

export const TenantActivityLog: React.FC<TenantActivityLogProps> = ({ tenantId }) => {
  const [logs, setLogs] = useState<AuditLogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [actionFilter, setActionFilter] = useState<string | undefined>();
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const fromDate = dateRange?.[0]?.format('YYYY-MM-DD');
      const toDate = dateRange?.[1]?.format('YYYY-MM-DD');
      const response = await tenantService.getAuditLogs(tenantId, {
        action: actionFilter,
        from_date: fromDate,
        to_date: toDate,
        page,
        limit,
      });
      setLogs(response.items);
      setTotal(response.meta.total);
    } catch {
      // silent fail — table shows empty
    } finally {
      setLoading(false);
    }
  }, [tenantId, actionFilter, dateRange, page]);

  useEffect(() => {
    if (tenantId) fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const handleFilterChange = () => {
    setPage(1);
    fetchLogs();
  };

  const columns: ColumnsType<AuditLogEntry> = [
    {
      title: 'Time',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (val: string) => dayjs(val).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (val: string) => (
        <Tag color={actionTagColor[val] ?? 'default'}>{val}</Tag>
      ),
    },
    {
      title: 'Admin ID',
      dataIndex: 'admin_id',
      key: 'admin_id',
      width: 160,
      render: (val: string) => <Text code>{val}</Text>,
    },
    {
      title: 'IP Address',
      dataIndex: 'ip_address',
      key: 'ip_address',
      width: 140,
    },
    {
      title: 'User Agent',
      dataIndex: 'user_agent',
      key: 'user_agent',
      ellipsis: true,
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={16}>
      <Space>
        <Select
          allowClear
          placeholder="Filter by action"
          style={{ width: 180 }}
          value={actionFilter}
          onChange={(val) => {
            setActionFilter(val);
            setPage(1);
          }}
          options={[
            { label: 'Impersonate', value: 'impersonate' },
            { label: 'Sync MISA', value: 'sync_misa' },
          ]}
        />
        <RangePicker
          value={dateRange}
          onChange={(dates) => {
            setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs] | null);
            setPage(1);
          }}
        />
        <Button onClick={handleFilterChange}>Refresh</Button>
      </Space>

      <Table
        columns={columns}
        dataSource={logs}
        rowKey="id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          onChange: setPage,
          showSizeChanger: false,
        }}
        locale={{ emptyText: 'No activity logs found' }}
        size="small"
      />
    </Space>
  );
};

export default TenantActivityLog;